import { useMemo } from 'react';

import {
  CAREER_HISTORY,
  EDUCATION,
  LANGUAGES,
  LICENCES,
  PERSONAL_DETAILS,
  SKILLS,
  SUMMARY,
  VERIFIED_IDENTITY,
} from 'src/components/Router';
import { useMutation, useQuery } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import { isGraphQLAuthError } from 'src/modules/error-handling/error-handling';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import * as Schema from 'src/types/graphql';

import { getQueryParams } from '../../../modules/utils';
import { useGetShareableProfileStatus } from '../ProfileVisibilityV2/data';

export const useShareableProfile = () => {
  const { zone, language: languageCode } = useAppConfig();
  const shareableProfileSlug = useTypedSelector(
    (state) => state.router.shareableProfileSlug,
  );

  const { loading, error, data } = useQuery(
    Schema.GetShareableProfileDocument,
    {
      variables: {
        slug: shareableProfileSlug,
        zone,
        languageCode,
      },
    },
  );

  const shareableProfile =
    loading || error ? null : data?.shareableProfile || null;

  const shareableProfileCardEmpty = useMemo(
    () => ({
      [CAREER_HISTORY]: (shareableProfile?.roles || []).length === 0,
      [EDUCATION]: (shareableProfile?.qualifications || []).length === 0,
      [LANGUAGES]: (shareableProfile?.languageProficiencies || []).length === 0,
      [LICENCES]: (shareableProfile?.licences || []).length === 0,
      [PERSONAL_DETAILS]:
        shareableProfile?.personalDetails.firstName === null ||
        shareableProfile?.personalDetails.lastName === null,
      [SKILLS]: (shareableProfile?.skills || []).length === 0,
      [SUMMARY]: shareableProfile?.summary === null,
      [VERIFIED_IDENTITY]: shareableProfile?.verifiedIdentity === null,
    }),
    [shareableProfile],
  );

  return {
    shareableProfileCardEmpty,
    shareableProfile,
    loading,
    error,
  };
};

export const useUpdateShareableProfileViews = () => {
  const shareableProfileSlug = useTypedSelector(
    (state) => state.router.shareableProfileSlug,
  );

  const [updateShareableProfileViews] = useMutation(
    Schema.UpdateShareableProfileViewsDocument,
    {
      variables: {
        slug: shareableProfileSlug,
      },
    },
  );

  return { updateShareableProfileViews };
};

export const useShareableProfilePage = () => {
  // Retrieve shareable profile data
  const {
    shareableProfile,
    loading: isShareableProfileLoading,
    error,
  } = useShareableProfile();

  const preview = useMemo(
    () =>
      getQueryParams(typeof window !== 'undefined' ? window.location.href : '')
        .preview === 'true',
    [],
  );

  // Retrieve logged in user's profileSlug
  // In order to verify whether it is own profile view
  const {
    shareableProfileSlug,
    loading: isShareableProfileSettingLoading,
    error: shareableProfileSettingError,
  } = useGetShareableProfileStatus(true);

  const isLoading =
    isShareableProfileLoading || isShareableProfileSettingLoading;

  const isActive = shareableProfile?.active || false;

  const isShareableProfileSettingAuthError =
    shareableProfileSettingError &&
    isGraphQLAuthError(shareableProfileSettingError);

  const isSelfView =
    preview ||
    (isShareableProfileSettingAuthError
      ? false
      : shareableProfileSlug === shareableProfile?.id);

  const hasVerification = (): boolean => {
    const hasVerifiedLicence = shareableProfile?.licences.some(
      (licence) => licence.isVerified,
    );
    const hasVerifiedQualification = shareableProfile?.qualifications.some(
      (qualification) => qualification.isVerified,
    );
    const hasVerifiedIdentity = Boolean(
      shareableProfile?.verifiedIdentity?.name,
    );

    return (
      hasVerifiedLicence || hasVerifiedQualification || hasVerifiedIdentity
    );
  };

  const showCertsyFooter = !isLoading && hasVerification();

  return {
    isLoading,
    isActive,
    isSelfView,
    error,
    showCertsyFooter,
  };
};
